import {
  ColumnDelete,
  ColumnInsert,
  RowDelete,
  RowInsert,
} from '@carbon/icons-react'
import { useFocus } from 'app/_components/Editor/useFocus'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'app/_components/Popover'
import SelectPrimitive, {
  SelectItem as SelectItemPrimitve,
} from 'app/_components/Select'
import { Tooltip } from 'app/_components/Tooltip'
import { Fragment } from 'react'
import { useSlate } from 'slate-react'
import { twMerge } from 'tailwind-merge'
import {
  isBlockActive,
  isListActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
} from '../extensions/formatting'
import { insertFlowElement } from '../extensions/withFlow'
import { LinkForm } from '../extensions/withLinks/LinkForm'
import {
  addColumn,
  addRow,
  insertTable,
  isInTable,
  removeColumn,
  removeRow,
} from '../extensions/withTables'
import { getTextColor, setTextColor } from '../extensions/withTextColor'
import { handleFiles } from '../extensions/withUploads'
import { type Block, type Mark } from '.'
import { LABEL_MAP } from './labels'
import styles from './styles.module.css'

const CustomTooltip: typeof Tooltip = ({ ...props }) => {
  const Wrapper = props.content ? Tooltip : Fragment
  return <Wrapper delayDuration={200} {...props} />
}

type ButtonProps = {
  action: Mark | Block | 'undo' | 'redo'
  className?: string
  options?: Record<string, string>
}

export function Button({ action, className, options }: ButtonProps) {
  const editor = useSlate()
  const focus = useFocus()
  const isCursorInTable = isInTable(editor)
  const [Icon, title] = LABEL_MAP[action]

  const prevented =
    (callback: () => void) =>
    (event: React.MouseEvent): void => {
      event.preventDefault()
      callback()
    }

  switch (action) {
    case 'bold':
    case 'italic':
      return (
        <CustomTooltip content={title}>
          <button
            data-active={isMarkActive(editor, action as Mark)}
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            type="button"
            onMouseDown={prevented(() => toggleMark(editor, action as Mark))}
          >
            <span className="sr-only">{title}</span>
            <Icon />
          </button>
        </CustomTooltip>
      )
    case 'numbered-list':
    case 'bulleted-list':
      return (
        <CustomTooltip content={title}>
          <button
            disabled={isCursorInTable}
            data-active={isListActive(editor, action as Block)}
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            type="button"
            onMouseDown={prevented(() => toggleBlock(editor, action as Block))}
          >
            <span className="sr-only">{title}</span>
            <Icon />
          </button>
        </CustomTooltip>
      )
    case 'block-quote':
    case 'heading-one':
    case 'heading-two':
    case 'heading-three':
    case 'paragraph':
      return (
        <CustomTooltip content={title}>
          <button
            disabled={isCursorInTable}
            data-active={isBlockActive(editor, action as Block)}
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            type="button"
            onMouseDown={prevented(() => toggleBlock(editor, action as Block))}
          >
            <span className="sr-only">{title}</span>
            <Icon />
          </button>
        </CustomTooltip>
      )
    case 'undo':
    case 'redo':
      const isDisabled =
        action === 'redo'
          ? editor.history.redos.length === 0
          : editor.history.undos.length === 0
      const handleMouseDown = action === 'redo' ? editor.redo : editor.undo

      return (
        <CustomTooltip content={title}>
          <button
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            type="button"
            onMouseDown={prevented(handleMouseDown)}
            disabled={isDisabled}
          >
            <div className="sr-only">{title}</div>
            <Icon />
          </button>
        </CustomTooltip>
      )
    case 'file':
    case 'file-block':
    case 'image':
    case 'video':
      let accept = undefined
      if (action === 'image') {
        accept = 'image/*'
      } else if (action === 'video') {
        accept = 'video/*'
      }

      return (
        <CustomTooltip content={title}>
          <label
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            tabIndex={0}
          >
            <span className="sr-only">{title}</span>
            <Icon />
            <input
              disabled={isCursorInTable}
              type="file"
              accept={accept}
              onChange={(event) => {
                if (event.target.files) {
                  handleFiles(editor, event.target.files, action)
                }
                // allows selecting the same file again
                event.target.value = ''
              }}
              className="hidden"
            />
          </label>
        </CustomTooltip>
      )
    case 'link':
      return (
        <CustomTooltip content={title}>
          {/* this div prevents react from an ref error in frontend while maintaninig the tooltip function */}
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-active={isBlockActive(editor, action as Block)}
                  className={twMerge(
                    'button hover:bg-gray-20',
                    styles.button,
                    className,
                  )}
                  type="button"
                >
                  <span className="sr-only">{title}</span>
                  <Icon />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="bg-gray-10 max-w-96"
                onCloseAutoFocus={focus}
              >
                <LinkForm />
              </PopoverContent>
            </Popover>
          </div>
        </CustomTooltip>
      )
    case 'table':
      return (
        <>
          {isCursorInTable ? (
            <>
              <CustomTooltip content="Reihe hinzufügen">
                <button
                  className={twMerge(
                    'button hover:bg-gray-20',
                    styles.button,
                    className,
                  )}
                  type="button"
                  onMouseDown={() => addRow(editor)}
                >
                  <span className="sr-only">{title}</span>
                  <RowInsert className="h-4 w-4" />
                </button>
              </CustomTooltip>
              <CustomTooltip content="Reihe entfernen">
                <button
                  className={twMerge(
                    'button hover:bg-gray-20',
                    styles.button,
                    className,
                  )}
                  type="button"
                  onMouseDown={() => removeRow(editor)}
                >
                  <span className="sr-only">{title}</span>
                  <RowDelete className="h-4 w-4" />
                </button>
              </CustomTooltip>
              <CustomTooltip content="Spalte hinzufügen">
                <button
                  className={twMerge(
                    'button hover:bg-gray-20',
                    styles.button,
                    className,
                  )}
                  type="button"
                  onMouseDown={() => addColumn(editor)}
                >
                  <span className="sr-only">{title}</span>
                  <ColumnInsert className="h-4 w-4" />
                </button>
              </CustomTooltip>
              <CustomTooltip content="Spalte entfernen">
                <button
                  className={twMerge(
                    'button hover:bg-gray-20',
                    styles.button,
                    className,
                  )}
                  type="button"
                  onMouseDown={() => removeColumn(editor)}
                >
                  <span className="sr-only">{title}</span>
                  <ColumnDelete className="h-4 w-4" />
                </button>
              </CustomTooltip>
            </>
          ) : (
            <CustomTooltip content={title}>
              <button
                className={twMerge(
                  'button hover:bg-gray-20',
                  styles.button,
                  className,
                )}
                type="button"
                onMouseDown={() => insertTable(editor)}
              >
                <span className="sr-only">{title}</span>
                <Icon />
              </button>
            </CustomTooltip>
          )}
        </>
      )
    case 'flow':
      return (
        <CustomTooltip content={title}>
          <button
            className={twMerge(
              'button hover:bg-gray-20',
              styles.button,
              className,
            )}
            type="button"
            disabled={isCursorInTable}
            onMouseDown={prevented(() => insertFlowElement(editor))}
          >
            <span className="sr-only">{title}</span>
            <Icon />
          </button>
        </CustomTooltip>
      )
    case 'color':
      return (
        <SelectPrimitive
          placeholder="Textfarbe"
          label="Textfarbe"
          onValueChange={(value) => {
            setTextColor(editor, value)
          }}
          value={getTextColor(editor) ?? 'inherit'}
          Trigger={(props) => (
            <SelectPrimitive.Trigger
              className={twMerge(
                'button hover:bg-gray-20',
                styles.button,
                className,
                props.className,
              )}
              aria-label={props.label}
            >
              <CustomTooltip content={title}>
                <SelectPrimitive.Icon className="h-4 w-4 text-black">
                  <Icon />
                </SelectPrimitive.Icon>
              </CustomTooltip>
            </SelectPrimitive.Trigger>
          )}
        >
          <SelectItemPrimitve value="inherit">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-gray-800" />
              Schwarz
            </div>
          </SelectItemPrimitve>
          {Object.entries(options ?? {}).map(([key, value]) => {
            return (
              <SelectItemPrimitve value={value} key={key}>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4" style={{ backgroundColor: value }} />
                  {key}
                </div>
              </SelectItemPrimitve>
            )
          })}
        </SelectPrimitive>
      )

    default:
      console.error(
        'Following action not implemented as toolbar button:',
        action,
      )
      throw new Error('Not implemented')
  }
}
